<template>
    <HomeSectionContainer :title="t('settings.deleteAccount.title')">
        <template #subtitle>
            <p class="body-small text-ink-muted mt-1">{{ t("settings.deleteAccount.subtitle") }}</p>
        </template>

        <BaseButton theme="danger" size="sm" data-testid="delete-account-button" @click="showConfirmDialog = true"> {{ t("settings.deleteAccount.deleteButton") }} </BaseButton>

        <!-- Step 1: Consequences confirmation -->
        <ConfirmationDialog
            v-model:open="showConfirmDialog"
            :title="t('settings.deleteAccount.consequencesDialog.title')"
            :confirm-label="t('settings.deleteAccount.consequencesDialog.continueButton')"
            :cancel-label="t('common.cancel')"
            container-class="md:max-w-2xl"
            @confirm="advanceToEmailConfirm"
            @cancel="showConfirmDialog = false">
            <template #content>
                <div class="flex flex-col gap-3">
                    <DeletionConsequences />
                </div>
            </template>
        </ConfirmationDialog>

        <!-- Step 2: Email confirmation -->
        <ConfirmationDialog
            v-model:open="showEmailConfirmDialog"
            :title="t('settings.deleteAccount.confirmDialog.title')"
            :confirm-label="isSubmitting ? t('settings.deleteAccount.deletingButton') : t('settings.deleteAccount.deleteButton')"
            :cancel-label="t('common.cancel')"
            confirm-theme="danger"
            container-class="md:max-w-2xl"
            :confirm-disabled="!canSubmit || isSubmitting"
            :close-on-confirm="false"
            @confirm="onFinalConfirm"
            @cancel="closeEmailConfirm">
            <template #content>
                <div class="flex flex-col gap-3 w-full">
                    <DeletionConsequences />
                    <label for="delete-account-note-input" class="body text-control-label flex flex-col gap-2">
                        <span>{{ t("settings.deleteAccount.confirmDialog.reasonLabel") }}</span>
                        <BaseTextarea
                            id="delete-account-note-input"
                            v-model:input="deletionNote"
                            rows="2"
                            :maxlength="2000"
                            :placeholder="t('settings.deleteAccount.confirmDialog.reasonPlaceholder')"
                            class="rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-field"
                            data-testid="delete-note-input" />
                    </label>
                    <label for="delete-account-email-input" class="body text-control-label flex flex-col gap-2">
                        <I18nT keypath="settings.deleteAccount.confirmDialog.emailLabel" tag="span">
                            <template #email>
                                <strong>{{ userEmail }}</strong>
                            </template>
                        </I18nT>
                        <input
                            id="delete-account-email-input"
                            v-model="emailInput"
                            type="email"
                            class="w-full rounded-xl border border-control-border bg-control-bg px-3 py-2 body text-control-text-strong focus-visible:border-control-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong"
                            :placeholder="t('settings.deleteAccount.confirmDialog.emailPlaceholder')"
                            data-testid="delete-email-input"
                            @keydown.enter="handleEmailEnter" />
                    </label>
                    <BaseNotification v-if="errorMessage" theme="error" hide-dismiss data-testid="delete-error" :text="String(errorMessage)" />
                </div>
            </template>
        </ConfirmationDialog>
    </HomeSectionContainer>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import HomeSectionContainer from "~vue/ChatHome/HomeSectionContainer.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import DeletionConsequences from "~vue/UserProfile/DeletionConsequences.vue";
import { shutdownIntercomMessengerEverywhere } from "~vue/utils/intercom";
import { unlessComposing } from "~vue/utils/keyboard";
import type { Ref } from "vue";
import { computed, inject, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    deleteAccountUrl: string;
}>();

const userDetails = inject<Ref<UserDetails>>("user_details");
const userEmail = computed(() => userDetails?.value?.email ?? "");

const showConfirmDialog = ref(false);
const showEmailConfirmDialog = ref(false);
const emailInput = ref("");
const deletionNote = ref("");
// The server's own message wins when it sends one; otherwise the generic copy is resolved at render
// time so it follows a language switch.
const deleteError = ref<{ serverMessage: string | null } | null>(null);
const errorMessage = computed(() => (deleteError.value ? (deleteError.value.serverMessage ?? t("settings.deleteAccount.confirmDialog.genericError")) : ""));
const isSubmitting = ref(false);

const emailMatches = computed(() => emailInput.value.toLowerCase().trim() === userEmail.value?.toLowerCase().trim());
const canSubmit = computed(() => emailMatches.value && deletionNote.value.trim().length > 0);

function advanceToEmailConfirm() {
    showConfirmDialog.value = false;
    showEmailConfirmDialog.value = true;
}

function closeEmailConfirm() {
    showEmailConfirmDialog.value = false;
    emailInput.value = "";
    deletionNote.value = "";
    deleteError.value = null;
}

async function onFinalConfirm() {
    if (!canSubmit.value || isSubmitting.value) {
        return;
    }

    isSubmitting.value = true;
    deleteError.value = null;

    const { data, error } = await useFetch(props.deleteAccountUrl, { immediate: true })
        .post({ reason: deletionNote.value.trim() })
        .json<{ redirect_url?: string; error?: string }>();

    if (error.value || !data.value?.redirect_url) {
        isSubmitting.value = false;
        deleteError.value = { serverMessage: (data.value as { error?: string } | null)?.error ?? null };
        return;
    }

    // The account is gone and this tab leaves; another open tab must not keep showing its support chat.
    shutdownIntercomMessengerEverywhere();
    globalThis.location.href = data.value.redirect_url;
}

const handleEmailEnter = unlessComposing(onFinalConfirm);
</script>
